import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  Info,
  AlertTriangle,
  Github,
  ExternalLink,
  Building2,
  User,
} from 'lucide-react'

interface ExportToGithubDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: number
}

const getCsrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''

interface Installation {
  id: number
  installation_id: number
  account_login: string
  account_type: 'User' | 'Organization'
  account_id: number
}

interface Repository {
  id: number
  name: string
  full_name: string
  private: boolean
  html_url: string
  description: string | null
  default_branch: string
}

type DialogState =
  | 'loading'
  | 'no_installation'
  | 'select_repo'
  | 'exporting'
  | 'success'
  | 'error'

export function ExportToGithubDialog({
  open,
  onOpenChange,
  projectId,
}: ExportToGithubDialogProps) {
  const { t } = useTranslation('conversations')
  const [dialogState, setDialogState] = useState<DialogState>('loading')
  const [installations, setInstallations] = useState<Installation[]>([])
  const [selectedInstallationId, setSelectedInstallationId] = useState<string>('')
  const [repositories, setRepositories] = useState<Repository[]>([])
  const [selectedRepoFullName, setSelectedRepoFullName] = useState<string>('')
  const [loadingRepos, setLoadingRepos] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [exportedRepoUrl, setExportedRepoUrl] = useState<string | null>(null)

  // Fetch installations when dialog opens
  useEffect(() => {
    if (open) {
      fetchInstallations()
    }
  }, [open])

  // Fetch repositories when installation is selected
  useEffect(() => {
    if (selectedInstallationId) {
      fetchRepositories(selectedInstallationId)
    } else {
      setRepositories([])
      setSelectedRepoFullName('')
    }
  }, [selectedInstallationId])

  const fetchInstallations = async () => {
    setDialogState('loading')
    try {
      const response = await fetch('/github_app/installations', {
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
      })

      if (!response.ok) {
        throw new Error('Failed to fetch installations')
      }

      const data = await response.json()
      setInstallations(data.installations || [])

      if (data.installations && data.installations.length > 0) {
        setDialogState('select_repo')
        // Auto-select if only one installation
        if (data.installations.length === 1) {
          setSelectedInstallationId(String(data.installations[0].id))
        }
      } else {
        setDialogState('no_installation')
      }
    } catch (err) {
      console.error('Error fetching installations:', err)
      setError(t('export.errors.network'))
      setDialogState('error')
    }
  }

  const fetchRepositories = async (installationId: string) => {
    setLoadingRepos(true)
    setRepositories([])
    setSelectedRepoFullName('')

    try {
      const response = await fetch(`/github_app/repositories?installation_id=${installationId}`, {
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to fetch repositories')
      }

      const data = await response.json()
      setRepositories(data.repositories || [])
    } catch (err) {
      console.error('Error fetching repositories:', err)
      toast.error(err instanceof Error ? err.message : t('export.errors.network'))
    } finally {
      setLoadingRepos(false)
    }
  }

  const handleInstallApp = () => {
    // Redirect to GitHub App installation with return URL that auto-opens dialog
    const currentUrl = new URL(window.location.href)
    currentUrl.searchParams.set('open_export', 'true')
    const returnUrl = encodeURIComponent(currentUrl.toString())
    window.location.href = `/github_app/install?return_url=${returnUrl}`
  }

  const handleExport = async () => {
    if (!selectedInstallationId || !selectedRepoFullName) {
      setError(t('export.errors.fillAllFields'))
      return
    }

    setDialogState('exporting')
    setError(null)

    try {
      const response = await fetch(`/projects/${projectId}/export_to_github`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({
          installation_id: selectedInstallationId,
          repository_full_name: selectedRepoFullName,
        }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setExportedRepoUrl(data.repo_url)
        setDialogState('success')
        toast.success(t('export.success'))
      } else {
        const errorMsg = data.error || t('export.errors.unknown')
        setError(errorMsg)
        setDialogState('error')
      }
    } catch (err) {
      console.error('Export to GitHub error:', err)
      setError(t('export.errors.network'))
      setDialogState('error')
    }
  }

  const handleTryAgain = () => {
    setError(null)
    fetchInstallations()
  }

  const resetDialog = () => {
    setDialogState('loading')
    setInstallations([])
    setSelectedInstallationId('')
    setRepositories([])
    setSelectedRepoFullName('')
    setLoadingRepos(false)
    setError(null)
    setExportedRepoUrl(null)
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetDialog()
    }
    onOpenChange(newOpen)
  }

  const selectedInstallation = installations.find(
    (inst) => String(inst.id) === selectedInstallationId
  )

  const renderContent = () => {
    switch (dialogState) {
      case 'loading':
        return (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">{t('common:loading')}</p>
          </div>
        )

      case 'no_installation':
        return (
          <>
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
                <Github className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="text-center space-y-2">
                <h3 className="text-lg font-semibold">{t('export.noInstallation.title')}</h3>
                <p className="text-sm text-muted-foreground max-w-sm">
                  {t('export.noInstallation.description')}
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                {t('common:cancel')}
              </Button>
              <Button onClick={handleInstallApp}>
                <Github className="mr-2 h-4 w-4" />
                {t('export.noInstallation.button')}
              </Button>
            </DialogFooter>
          </>
        )

      case 'select_repo':
        return (
          <>
            <div className="space-y-4 py-4">
              {/* Account Selector (only show if multiple) */}
              {installations.length > 1 && (
                <div className="space-y-2">
                  <Label>{t('export.selectAccount')}</Label>
                  <Select
                    value={selectedInstallationId}
                    onValueChange={setSelectedInstallationId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('export.selectAccount')} />
                    </SelectTrigger>
                    <SelectContent>
                      {installations.map((inst) => (
                        <SelectItem key={inst.id} value={String(inst.id)}>
                          <div className="flex items-center gap-2">
                            {inst.account_type === 'Organization' ? (
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <User className="h-4 w-4 text-muted-foreground" />
                            )}
                            <span>{inst.account_login}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Single installation indicator */}
              {installations.length === 1 && selectedInstallation && (
                <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                  {selectedInstallation.account_type === 'Organization' ? (
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <User className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="font-medium">{selectedInstallation.account_login}</span>
                </div>
              )}

              {/* Repository Selector */}
              <div className="space-y-2">
                <Label>{t('export.selectRepo')}</Label>
                {loadingRepos ? (
                  <div className="flex items-center gap-2 p-3 border rounded-md">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm text-muted-foreground">
                      {t('export.loadingRepos')}
                    </span>
                  </div>
                ) : (
                  <Select
                    value={selectedRepoFullName}
                    onValueChange={setSelectedRepoFullName}
                    disabled={!selectedInstallationId || repositories.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('export.selectRepo')} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto">
                      {repositories.map((repo) => (
                        <SelectItem key={repo.id} value={repo.full_name}>
                          <div className="flex flex-col">
                            <span>{repo.name}</span>
                            {repo.description && (
                              <span className="text-xs text-muted-foreground truncate max-w-[300px]">
                                {repo.description}
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {selectedInstallationId && !loadingRepos && repositories.length === 0 && (
                  <p className="text-xs text-muted-foreground">{t('export.noReposAvailable')}</p>
                )}
              </div>

              {/* Info Box */}
              <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 rounded-md">
                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <p className="font-medium mb-1">{t('export.infoWhatGetsExported')}</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>{t('export.infoCleanedCode')}</li>
                    <li>{t('export.infoNoInfrastructure')}</li>
                    <li>{t('export.infoSingleCommit')}</li>
                  </ul>
                </div>
              </div>

              {/* Warning Box */}
              <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-md">
                <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-800 dark:text-red-200">{t('export.warning')}</p>
              </div>

              {/* Error Display */}
              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-md">
                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                {t('common:cancel')}
              </Button>
              <Button
                onClick={handleExport}
                disabled={!selectedInstallationId || !selectedRepoFullName}
              >
                <Github className="mr-2 h-4 w-4" />
                {t('export.exportButton')}
              </Button>
            </DialogFooter>
          </>
        )

      case 'exporting':
        return (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold">{t('export.exporting')}</h3>
              <p className="text-sm text-muted-foreground">{t('export.exportingDescription')}</p>
            </div>
          </div>
        )

      case 'success':
        return (
          <>
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <div className="text-center space-y-2">
                <h3 className="text-lg font-semibold">{t('export.success')}</h3>
                <p className="text-sm text-muted-foreground">{t('export.successDescription')}</p>
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                {t('common:close')}
              </Button>
              {exportedRepoUrl && (
                <Button asChild>
                  <a href={exportedRepoUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    {t('export.viewOnGithub')}
                  </a>
                </Button>
              )}
            </DialogFooter>
          </>
        )

      case 'error':
        return (
          <>
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="h-16 w-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>
              <div className="text-center space-y-2">
                <h3 className="text-lg font-semibold">{t('export.failed')}</h3>
                <p className="text-sm text-muted-foreground max-w-sm">{error}</p>
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                {t('common:cancel')}
              </Button>
              <Button onClick={handleTryAgain}>{t('export.tryAgain')}</Button>
            </DialogFooter>
          </>
        )
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Github className="h-5 w-5" />
            {t('export.title')}
          </DialogTitle>
          {dialogState === 'select_repo' && (
            <DialogDescription>{t('export.description')}</DialogDescription>
          )}
        </DialogHeader>

        {renderContent()}
      </DialogContent>
    </Dialog>
  )
}
